import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';

import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Location } from '@angular/common';

import { ClientService } from '../client/client.service';

const PUBLIC_ROUTES = ['/set-password', '/reset-password', '/set-client', '/sign-in', '/remote-access'];

// How the current session was established. 'password' covers the normal
// sign-in and remote-access flows; 'devicePin' is a PIN unlock on a device
// that was authorized for PIN sign-in (see DeviceAuthService).
export type AuthMethod = 'password' | 'devicePin';

// Why the last session ended, so the sign-in screen can explain itself.
export type SignoutReason = 'user' | 'expired' | 'idle' | 'deviceRevoked' | 'relaunch' | 'authorized' | null;

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private authenticatedId;
  private authenticationStateChanged = new Subject<any>();
  private lastSignoutReason: SignoutReason = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private location: Location,
    private clientService: ClientService
  ) {
    console.log('Authentication Service constructor()');

    // A PIN-unlocked session does not survive a relaunch of the app: closing
    // and reopening the PWA on a shared handheld always asks for the PIN
    // again. The device authorization itself (a separate localStorage key
    // owned by DeviceAuthService) is untouched, so the PIN pad comes right up.
    if (localStorage.getItem('authMethod') === 'devicePin') {
      this.clearSessionStorage();
      this.lastSignoutReason = 'relaunch';
    }

    if (Math.floor(Date.now() / 1000) < parseInt(localStorage.getItem('tokenEndOfLife'))) {
      this.authenticatedId = localStorage.getItem('authenticatedId');
      this.authenticationStateChange(this.authenticatedId);
    }
  }

  isSignedIn() {
    if (Math.floor(Date.now() / 1000) < parseInt(localStorage.getItem('tokenEndOfLife'))) {
      return true;
    } else {
      this.signout(localStorage.getItem('token') ? 'expired' : null);
      return false;
    }
  }

  // Send login credentials to the server to get a token
  signin(email: string, password: string) {
    return new Promise<void>((resolve, reject) => {
      this.http.post<any>(this.clientService.getServerPath() + 'authentication/signIn', {
        rfId: this.clientService.getRfId(),
        email,
        password
      }, {}).toPromise()
        .then(signinResponse => {
          if (signinResponse.token) {
            this.applySession(signinResponse, 'password');
            resolve();
          } else {
            console.error('login succeeded but did not return a token');
            reject();
          }
        })
        .catch(err => {
          // console.error('login failed');
          reject(err);
        });
    });
  }

  validateRemoteAccess(remoteAccessKey: string) {
    console.log('VALIDATE REMOTE ACCESS ('+this.clientService.getRfId()+')');


    return new Promise<void>((resolve, reject) => {
      this.http.post<any>(this.clientService.getServerPath() + 'authentication/validateRemoteAccess', {
        rfId: this.clientService.getRfId(),
        remoteAccessKey
      }, {}).toPromise()
        .then(signinResponse => {
          if (signinResponse.token) {
            this.applySession(signinResponse, 'password');
            resolve();
          } else {
            console.error('login succeeded but did not return a token');
            reject();
          }
        })
        .catch(err => {
          // console.error('login failed');
          reject(err);
        });
    });

  }

  // Exchange this device's authorization plus a user's PIN for a session.
  // The device key travels only in the Authorization header ("Device"
  // scheme) and only to this endpoint. Rejections carry a `code` the caller
  // uses to decide whether the device authorization itself is gone.
  devicePinIn(deviceKey: string, pin: string) {
    return new Promise<any>((resolve, reject) => {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: 'Device ' + deviceKey
        })
      };
      this.http.post<any>(this.clientService.getServerPath() + 'authentication/devicePinIn', {
        rfId: this.clientService.getRfId(),
        pin
      }, httpOptions).toPromise()
        .then(pinResponse => {
          if (pinResponse.token) {
            this.applySession(pinResponse, 'devicePin');
            resolve(pinResponse);
          } else {
            console.error('PIN sign-in succeeded but did not return a token');
            reject({ error: { msg: 'Sign-in failed. Please try again.' } });
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  // Persist a freshly issued session and notify subscribers.
  private applySession(response: any, method: AuthMethod) {
    this.authenticatedId = response.userId;
    localStorage.setItem('token', response.token);
    localStorage.setItem('tokenEndOfLife', String(Math.floor(Date.now() / 1000) + response.tokenLifespan));
    localStorage.setItem('authenticatedId', this.authenticatedId);
    localStorage.setItem('authMethod', method);

    if (method === 'devicePin') {
      localStorage.setItem('sessionLocationId', response.locationId || '');
      localStorage.setItem('idleLockMinutes', String(response.idleLockMinutes || ''));
    } else {
      localStorage.removeItem('sessionLocationId');
      localStorage.removeItem('idleLockMinutes');
    }

    this.lastSignoutReason = null;
    this.authenticationStateChange(this.authenticatedId);
  }

  private clearSessionStorage() {
    localStorage.removeItem('tokenEndOfLife');
    localStorage.removeItem('token');
    localStorage.removeItem('authenticatedId');
    localStorage.removeItem('authMethod');
    localStorage.removeItem('sessionLocationId');
    localStorage.removeItem('idleLockMinutes');
  }

  signout(reason: SignoutReason = 'user') {
    const hadSession = !!localStorage.getItem('token');
    this.clearSessionStorage();
    // Record why, but never let a no-op sign-out (isSignedIn() on the sign-in
    // page itself, with no session) erase a pending reason such as 'idle' or
    // 'relaunch' before the sign-in screen has shown it.
    if (reason && (hadSession || reason !== 'user')) {
      this.lastSignoutReason = reason;
    }
    this.authenticatedId = null;
    this.authenticationStateChange(this.authenticatedId);
    const currentPath = this.location.path().split('?')[0];
    if (!PUBLIC_ROUTES.includes(currentPath)) {
      this.router.navigate(['/sign-in']);
    }
  }

  // Locking is a sign-out that keeps the device authorization, so the PIN
  // pad is what comes back. Named separately for readability at call sites.
  lock(reason: SignoutReason = 'user') {
    this.signout(reason);
  }

  getAuthMethod(): AuthMethod | null {
    const method = localStorage.getItem('authMethod');
    return (method === 'devicePin' || method === 'password') ? method : null;
  }

  isDeviceSession(): boolean {
    return this.getAuthMethod() === 'devicePin';
  }

  // Location the device is authorized for (device-PIN sessions only).
  getSessionLocationId(): string | null {
    return localStorage.getItem('sessionLocationId') || null;
  }

  // Idle lock threshold delivered with the PIN sign-in (device-PIN sessions only).
  getIdleLockMinutes(): number {
    const minutes = parseInt(localStorage.getItem('idleLockMinutes'), 10);
    return (!isNaN(minutes) && minutes > 0) ? minutes : 0;
  }

  // Read-once: the sign-in screen shows the reason and clears it.
  consumeSignoutReason(): SignoutReason {
    const reason = this.lastSignoutReason;
    this.lastSignoutReason = null;
    return reason;
  }

  setPassword(password: string, key: string) {
    console.log('setPassword: ', password);
    return new Promise<void>((resolve, reject) => {
      this.http.post<any>(this.clientService.getServerPath() + 'authentication/setPassword', {
        password,
        key,
        rfId: this.clientService.getRfId(),
      }, {}).toPromise()
        .then(resetResponse => {
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  requestPasswordReset(userKey: string) {
    console.log('requestPasswordReset: ', userKey);
    return new Promise<void>((resolve, reject) => {
      this.http.post<any>(this.clientService.getServerPath() + 'authentication/requestPasswordReset', {
        userKey,
        rfId: this.clientService.getRfId(),
      }, {}).toPromise()
        .then(resetResponse => {
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  // The signed-in user's id (survives a reload via localStorage).
  getAuthenticatedId(): string | null {
    return this.authenticatedId || localStorage.getItem('authenticatedId') || null;
  }

  authenticationStateChange(authenticatedId) {
    this.authenticationStateChanged.next(authenticatedId);
  }

  authenticationStateChangedObservable(): Observable<any> {
    return this.authenticationStateChanged.asObservable();
  }

}
